import { Close } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'

import { useTranslate } from '@tolgee/react'
import { CarrierVisitDirection } from '@yard/app/api'
import { YARD_DRAWER_HEADER_HEIGHT } from '@yard/pages/YardSetup/helpers/yard-view.utils'
import { useUnsavedChangesGuard } from '@yard/pages/YardSetup/hooks/use-unsaved-changes-guard'
import { CarrierVisitAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/global-allocation-rules.view-store'
import { CarrierVisitOverviewViewStore } from '@yard/pages/YardSetup/stores/carrier-visit-overview.viewstore'
import { YardViewStore } from '@yard/pages/YardSetup/stores/yard.view-store'
import { observer } from 'mobx-react-lite'
import { CarrierVisitOverviewCardsByDate } from '../../molecules/CarrierVisitOverview/CarrierVisitOverviewCardsByDate'
import { CarrierVisitOverviewFilters } from '../../molecules/CarrierVisitOverview/CarrierVisitOverviewFilters'
import { UnsavedChangesDialog } from '../../molecules/UnsavedChangesDialog'

type Props = {
  yardViewStore: YardViewStore
  carrierVisitOverviewViewStore: CarrierVisitOverviewViewStore
  carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore
  globalAllocationRulesViewStore: GlobalAllocationRulesViewStore
}

export const CarrierVisitOverview = observer((props: Props) => {
  const {
    carrierVisitOverviewViewStore,
    carrierVisitAllocationRulesViewStore,
    globalAllocationRulesViewStore,
    yardViewStore,
  } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { showUnsavedChangesDialog, attemptAction, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard()

  const openCarrierVisitRules = (carrierVisitId: number, direction: CarrierVisitDirection) => {
    const isMatchingContext =
      carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen &&
      carrierVisitAllocationRulesViewStore.selectedCarrierVisitId === carrierVisitId &&
      carrierVisitAllocationRulesViewStore.selectedCarrierVisitDirection === direction

    if (isMatchingContext) {
      carrierVisitOverviewViewStore.setOverviewOpen(false)
      return
    }

    const hasUnsaved =
      globalAllocationRulesViewStore.hasUnsavedChanges ||
      carrierVisitAllocationRulesViewStore.hasUnsavedChanges

    const action = () => {
      if (globalAllocationRulesViewStore.hasUnsavedChanges) {
        globalAllocationRulesViewStore.resetChanges()
      }
      if (carrierVisitAllocationRulesViewStore.hasUnsavedChanges) {
        carrierVisitAllocationRulesViewStore.resetChanges()
      }
      carrierVisitAllocationRulesViewStore.openCarrierVisitRules(carrierVisitId, direction)
      globalAllocationRulesViewStore.setAllocationRulesDrawerOpen(false)
      carrierVisitOverviewViewStore.setOverviewOpen(false)
    }

    const dispose = () => {
      globalAllocationRulesViewStore.resetChanges()
      carrierVisitAllocationRulesViewStore.resetChanges()
    }

    attemptAction(hasUnsaved, action, dispose)
  }

  return (
    <>
      <Stack direction='column' height='100%'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          p={theme.customSpacing.s}
          height={YARD_DRAWER_HEADER_HEIGHT}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <Typography variant='subtitle1'>{t('carrierVisits', 'Carrier visits')}</Typography>
          <IconButton
            onClick={() => {
              carrierVisitOverviewViewStore.setOverviewOpen(false)
              yardViewStore.setShowAllocationRulesPreview(false)
            }}
          >
            <Close fontSize='small' />
          </IconButton>
        </Stack>
        <CarrierVisitOverviewFilters {...props} />

        <Stack sx={{ flex: 1, overflowY: 'auto' }}>
          {Object.entries(carrierVisitOverviewViewStore.carrierVisitsGroupedByDate).map(
            ([date, visits]) => (
              <CarrierVisitOverviewCardsByDate
                key={date}
                date={date}
                carrierVisits={visits}
                onEdit={openCarrierVisitRules}
              />
            ),
          )}
        </Stack>
      </Stack>

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  )
})
